/**
 * Finance › Awaiting payment, `/finance/awaiting-payment`
 * (docs/features/order-to-invoice/50_finance_sales_ledger_module_spec.md §9
 * item 5): the pro-formas still asking for money, issued and part paid,
 * oldest valid-until first, each with Record payment for finance (a bank
 * transfer read off Xero's bank feed, matched on the `PF-` number).
 *
 * No mock-up was drawn for this page: it is the smallest page consistent with
 * the other finance lists (the All purchase invoices index) and the order page's Record
 * payment (round 19). Search, filter, sort and paging run on the server
 * (`listAwaitingPayment`, docs/list-endpoints.md); this page holds them in the
 * URL, so a filtered list is a link. Changing any of them goes back to page 1.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse } from "@shared-core/data/adapter";
import type {
  AwaitingPaymentItem,
  AwaitingPaymentState,
} from "@shared-core/data/sales-ledger-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { RecordPaymentDialog } from "../app/components/domain/orders/OrderDialogs";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { cn } from "../app/components/primitives/utils";

const PAGE_SIZE = 50;
const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

const STATE_OPTIONS: { value: AwaitingPaymentState; label: string }[] = [
  { value: "issued", label: "Nothing paid yet" },
  { value: "part_paid", label: "Part paid" },
];
const SORT_OPTIONS = [
  { value: "valid_until", label: "Valid until, soonest first" },
  { value: "-outstanding_pence", label: "Most owed first" },
  { value: "-issued_at", label: "Newest first" },
  { value: "customer_name", label: "Customer A–Z" },
] as const;
type SortValue = (typeof SORT_OPTIONS)[number]["value"];

function readFilters(params: URLSearchParams) {
  const state = params.get("state");
  const sort = params.get("sort");
  return {
    state: STATE_OPTIONS.some((o) => o.value === state)
      ? (state as AwaitingPaymentState)
      : undefined,
    sort: SORT_OPTIONS.some((o) => o.value === sort)
      ? (sort as SortValue)
      : undefined,
    q: params.get("q") || undefined,
    page: Math.max(1, Number(params.get("page")) || 1),
  };
}

type ListState = {
  /** The last page that arrived: kept on screen while the next loads. */
  list: ListResponse<AwaitingPaymentItem> | null;
  isLoading: boolean;
  error: string | null;
};

export function AwaitingPaymentPage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [params, setParams] = useSearchParams();
  const { state, sort, q, page } = readFilters(params);
  const isFiltered = Boolean(state || q);

  const setFilter = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );

  // The search box is typed into freely and written to the URL once it settles.
  const [query, setQuery] = useState(q ?? "");
  const debouncedQuery = useDebouncedValue(query, 300).trim();
  useEffect(() => {
    if (debouncedQuery !== (q ?? "")) setFilter("q", debouncedQuery || null);
    // The URL's own q is the other direction; only a settled box writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const [listState, setListState] = useState<ListState>({
    list: null,
    isLoading: true,
    error: null,
  });
  const [reloads, setReloads] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setListState((s) => ({ ...s, isLoading: true, error: null }));
    adapter
      .listAwaitingPayment({
        state: state ? [state] : undefined,
        q,
        sort,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((list) => {
        if (!cancelled) setListState({ list, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setListState((s) => ({
            ...s,
            isLoading: false,
            error:
              err instanceof Error
                ? err.message
                : "The pro-formas did not load.",
          }));
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, state, q, sort, page, reloads]);

  const [paying, setPaying] = useState<AwaitingPaymentItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const viewPdf = useCallback(
    async (row: AwaitingPaymentItem) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } = await adapter.downloadSalesDocumentPdf(
          row.pdf_url,
        );
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  const { list } = listState;
  const summary = list
    ? `${list.total.toLocaleString("en-GB")} ${list.total === 1 ? "pro-forma" : "pro-formas"}${isFiltered ? " match" : " awaiting payment"}`
    : undefined;

  return (
    <div className="flex h-full flex-col">
      <header className="px-6 pt-6 pb-4">
        <h1 className="text-foreground text-2xl">Awaiting payment</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
          Pro-formas the customer has not paid in full, soonest to expire first.
          Match a bank line in Xero&apos;s bank feed by the line&apos;s
          reference (the pro-forma&apos;s number) and record the payment here.
          Once the pro-forma is paid in full, One will raise the invoice, marked
          paid, and the order will go ahead.
        </p>
        {summary && (
          <p className="text-muted-foreground mt-2 text-sm tabular-nums">
            {summary}
          </p>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b px-6 pb-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pro-forma, order number or customer…"
          aria-label="Search pro-formas awaiting payment"
          className="h-9 w-full sm:w-80"
        />
        <select
          aria-label="Paid so far"
          value={state ?? ""}
          onChange={(e) => setFilter("state", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Nothing paid or part paid</option>
          {STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Sort pro-formas"
          value={sort ?? ""}
          onChange={(e) => setFilter("sort", e.target.value || null)}
          className={cn(selectClass, "sm:ml-auto")}
        >
          <option value="">Sort…</option>
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {notice && (
        <p role="status" className="text-muted-foreground px-6 pt-3 text-sm">
          {notice}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {listState.error && (
          <p role="alert" className="p-6 text-sm text-red-600">
            Could not load the pro-formas: {listState.error}
          </p>
        )}
        {!list && listState.isLoading && (
          <div className="space-y-2 p-6" aria-label="Loading the pro-formas">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {list &&
          (list.items.length === 0 ? (
            <div className="text-muted-foreground p-6 text-sm">
              {isFiltered
                ? "No pro-formas awaiting payment match."
                : "Nothing is awaiting payment."}
            </div>
          ) : (
            <div
              aria-busy={listState.isLoading}
              className={cn(
                "transition-opacity",
                listState.isLoading && "opacity-60",
              )}
            >
              <AwaitingTable
                rows={list.items}
                busy={busy}
                onViewPdf={(row) => void viewPdf(row)}
                onRecordPayment={setPaying}
              />
              <div className="px-6 py-3">
                <ListPagination
                  page={list.page}
                  totalPages={list.totalPages}
                  onPageChange={(next) =>
                    setFilter("page", next > 1 ? String(next) : null)
                  }
                />
              </div>
            </div>
          ))}
      </div>

      {paying && (
        <RecordPaymentDialog
          proforma={paying}
          busy={busy}
          onClose={() => setPaying(null)}
          onRecord={async (input) => {
            setBusy(true);
            try {
              const recorded = await adapter.recordProformaPayment(
                paying,
                input,
              );
              setNotice(recorded.summary);
              setReloads((n) => n + 1);
              return null;
            } catch (err) {
              return orderStepFailure(err);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

function AwaitingTable({
  rows,
  busy,
  onViewPdf,
  onRecordPayment,
}: {
  rows: AwaitingPaymentItem[];
  busy: boolean;
  onViewPdf: (row: AwaitingPaymentItem) => void;
  onRecordPayment: (row: AwaitingPaymentItem) => void;
}) {
  return (
    <Table className="[&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6">
      <TableHeader>
        <TableRow>
          <TableHead>Pro-forma</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead className="hidden text-right md:table-cell">
            Gross
          </TableHead>
          <TableHead className="hidden text-right md:table-cell">
            Paid
          </TableHead>
          <TableHead className="text-right">Owed</TableHead>
          <TableHead>Where it stands</TableHead>
          <TableHead className="hidden lg:table-cell">Valid until</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.proforma_no}>
            <TableCell className="font-medium">
              {row.proforma_no}
              <div className="text-muted-foreground text-xs font-normal">
                <Link
                  to={paths.orderDetail({ orderNo: row.order_no })}
                  className="hover:underline"
                >
                  {row.order_no}
                </Link>
              </div>
            </TableCell>
            <TableCell>
              {row.customer_name ?? row.customer_code}
              {row.payer_name && row.payer_name !== row.customer_name && (
                <div className="text-muted-foreground text-xs">
                  Paid by {row.payer_name}
                </div>
              )}
            </TableCell>
            <TableCell className="hidden text-right tabular-nums md:table-cell">
              {formatQuotedPrice(row.gross_pence)}
            </TableCell>
            <TableCell className="hidden text-right tabular-nums md:table-cell">
              {formatQuotedPrice(row.paid_pence)}
            </TableCell>
            <TableCell className="text-right font-medium tabular-nums">
              {formatQuotedPrice(row.outstanding_pence)}
            </TableCell>
            <TableCell>
              <Badge
                variant={row.state === "part_paid" ? "warning" : "neutral"}
                className="font-normal"
              >
                {row.state_label}
              </Badge>
            </TableCell>
            <TableCell className="hidden tabular-nums lg:table-cell">
              {formatShortDateOrDash(row.valid_until)}
            </TableCell>
            <TableCell>
              <span className="flex flex-wrap justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onViewPdf(row)}
                  aria-label={`View PDF of ${row.proforma_no}`}
                >
                  View PDF
                </Button>
                {row.record_payment_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onRecordPayment(row)}
                    aria-label={`Record payment against ${row.proforma_no}`}
                  >
                    Record payment
                  </Button>
                )}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
